// Date-range facet — emits a pair of >= / <= rules on a date field.
// Used by the v1.2 "Application date" card and reusable for any future
// date facet. Shares the per-rule semantics with NumericRangeFacet (one
// `>=` + one `<=`, both optional) so ActiveFilterChips and the planner
// don't need a special case.
import { useEffect, useState } from "react";
import { useExplorerStore } from "../../store/explorerStore";
import type { FilterGroup, FilterRule } from "../../types/inpact";
import { isGroup } from "../../lib/filterLogic";

interface Props {
  field: string; // e.g. "contact.Date_of_application__c"
  ariaLabel: string;
}

function rulesForField(group: FilterGroup, field: string): FilterRule[] {
  return group.rules.filter(
    (r): r is FilterRule => !isGroup(r) && r.field === field,
  );
}

function setRulesForField(
  group: FilterGroup,
  field: string,
  newRules: FilterRule[],
): FilterGroup {
  const others = group.rules.filter(
    (r) => isGroup(r) || (r as FilterRule).field !== field,
  );
  return { ...group, rules: [...others, ...newRules] };
}

function deriveFromRules(rules: FilterRule[]): { from: string; to: string } {
  const ge = rules.find((r) => r.op === ">=");
  const le = rules.find((r) => r.op === "<=");
  return {
    from: ge ? String(ge.value ?? "") : "",
    to: le ? String(le.value ?? "") : "",
  };
}

function buildRules(field: string, from: string, to: string): FilterRule[] {
  const rules: FilterRule[] = [];
  if (from) rules.push({ field, op: ">=", value: from });
  if (to) rules.push({ field, op: "<=", value: to });
  return rules;
}

export function DateRangeFacet({ field, ariaLabel }: Props) {
  const { filterGroup, setFilterGroup } = useExplorerStore();
  const existing = rulesForField(filterGroup, field);
  const initial = deriveFromRules(existing);

  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);

  // Resync inputs when the field's rules change from OUTSIDE this facet (e.g.
  // AnaMoni applies a date filter, or the user removes the chip). `initial`
  // only changes when filterGroup changes, so this is a no-op during local
  // edits (mid-typing doesn't touch the store) but corrects external changes —
  // otherwise a stale local value would resurrect a just-removed filter.
  useEffect(() => {
    setFrom(initial.from);
    setTo(initial.to);
  }, [initial.from, initial.to]);

  const commit = (nextFrom: string, nextTo: string) => {
    setFilterGroup(
      setRulesForField(filterGroup, field, buildRules(field, nextFrom, nextTo)),
    );
  };

  return (
    <div className="date-range" role="group" aria-label={ariaLabel}>
      <label className="date-range__row">
        <span className="date-range__label">From</span>
        <input
          type="date"
          aria-label={`${ariaLabel} from`}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            commit(e.target.value, to);
          }}
        />
      </label>
      <label className="date-range__row">
        <span className="date-range__label">To</span>
        <input
          type="date"
          aria-label={`${ariaLabel} to`}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            commit(from, e.target.value);
          }}
        />
      </label>
    </div>
  );
}
